/**
 * Game Context Provider
 * 
 * Provides global game state and dispatch function to all child components.
 * Uses React Context API to avoid prop drilling and centralize state management.
 * 
 * Educational Purpose:
 * - Demonstrates React Context API for global state
 * - Shows how to create type-safe context providers
 * - Illustrates the provider pattern in React
 * 
 * Why Context API?
 * - Built into React (no external dependencies)
 * - Sufficient for this app's complexity
 * - Easier to understand than Redux for learners
 * - Good performance with proper optimization
 */

import React, { createContext, useReducer, useContext, ReactNode, Dispatch } from 'react';
import { GameState, GameAction } from '@core/types';
import { gameReducer, initialGameState } from './GameReducer';

/**
 * Context value type
 * Includes both state and dispatch function
 */
interface GameContextType {
  state: GameState;
  dispatch: Dispatch<GameAction>;
}

/**
 * Create the context with undefined as default
 * This forces consumers to use the context within a provider
 */
const GameContext = createContext<GameContextType | undefined>(undefined);

/**
 * Props for the GameProvider component
 */
interface GameProviderProps {
  children: ReactNode;
}

/**
 * GameProvider Component
 * 
 * Wraps the application (or part of it) to provide game state.
 * All child components can access state and dispatch via useGameContext hook.
 * 
 * @param props - Component props
 * @returns Provider component
 */
export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  // Initialize reducer with initial state
  const [state, dispatch] = useReducer(gameReducer, initialGameState);

  // Memoize context value to prevent unnecessary re-renders
  // Only re-create when state or dispatch changes
  const value = React.useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state]
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

/**
 * Custom hook to access game context
 * 
 * This hook provides type-safe access to game state and dispatch.
 * It throws an error if used outside of GameProvider, making bugs
 * easier to catch during development.
 * 
 * Usage:
 * ```tsx
 * const { state, dispatch } = useGameContext();
 * ```
 * 
 * @returns Game context value
 * @throws Error if used outside GameProvider
 */
export const useGameContext = (): GameContextType => {
  const context = useContext(GameContext);

  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }

  return context;
};

/**
 * Custom hook for accessing only game state
 * Useful when component only needs to read state, not dispatch actions
 * 
 * @returns Current game state
 */
export const useGameState = (): GameState => {
  const { state } = useGameContext();
  return state;
};

/**
 * Custom hook for accessing only dispatch function
 * Useful when component only needs to dispatch actions, not read state
 * 
 * @returns Dispatch function
 */
export const useGameDispatch = (): Dispatch<GameAction> => {
  const { dispatch } = useGameContext();
  return dispatch;
};